import React from 'react'
import PropTypes from 'prop-types'
import "../styles/ExitModal.sass"
import { validateEmail } from '../../../../utils/validateEmail'
import { contactService } from '../../../../services/contactService'

const STORAGE_KEY = 'exitModalShown'
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000 // 1 week in milliseconds

class ExitIntentModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isVisible: false,
      hasShownBefore: false,
      firstName: '',
      email: '',
      isSubmitting: false
    }

    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.checkStoredState = this.checkStoredState.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
  }

  componentDidMount() {
    const { enabled } = this.props

    if (!enabled) return

    // Check if modal was shown in the last week
    this.checkStoredState()

    // Only add listener if not shown before
    if (!this.state.hasShownBefore) {
      document.addEventListener('mouseleave', this.handleMouseLeave)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mouseleave', this.handleMouseLeave)
  }

  checkStoredState() {
    if (typeof window === 'undefined') return

    const storedData = localStorage.getItem(STORAGE_KEY)
    
    if (storedData) {
      const { timestamp } = JSON.parse(storedData)
      const currentTime = new Date().getTime()
      const timeDiff = currentTime - timestamp

      // If less than a week has passed, mark as shown before
      if (timeDiff < ONE_WEEK_MS) {
        this.setState({ hasShownBefore: true })
      } else {
        // Clear old data if more than a week has passed
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }

  handleMouseLeave(e) {
    // Only trigger if mouse leaves from top of viewport
    if (e.clientY <= 0 && !this.state.isVisible && !this.state.hasShownBefore) {
      this.showModal()
    }
  }

  showModal() {
    this.setState({ isVisible: true })
    
    // Store timestamp in localStorage
    if (typeof window !== 'undefined') {
      const data = {
        timestamp: new Date().getTime()
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    }
  }

  closeModal() {
    this.setState({ isVisible: false })
    // Remove listener after closing
    document.removeEventListener('mouseleave', this.handleMouseLeave)
  }

  handleInputChange(field, value) {
    this.setState({ [field]: value })
  }

  async handleSubmit(e) {
    e.preventDefault()
    const { firstName, email, isSubmitting } = this.state
    const { redirectUrl } = this.props

    if (isSubmitting) return

    // Validate
    if (!firstName || !email || !validateEmail(email)) {
      alert('Please enter a valid name and email address')
      return
    }

    this.setState({ isSubmitting: true })

    try {
      // Submit to contact service
      await contactService.createOrUpdateContact(email, 'exit-modal', { firstName })
      
      // Open link in new tab
      if (redirectUrl) {
        window.open(redirectUrl, '_blank')
      }

      // Close modal
      this.closeModal()
    } catch (err) {
      console.error('Failed to submit exit modal form:', err)
      alert('Something went wrong. Please try again.')
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  render() {
    const { enabled, children } = this.props
    const { isVisible, firstName, email, isSubmitting } = this.state

    if (!enabled || !isVisible) {
      return null
    }

    // If custom children provided, use those
    if (children) {
      return (
        <>
          <div className="exit-modal-overlay" onClick={this.closeModal} />
          <div className="exit-modal-container">
            <button 
              className="exit-modal-close" 
              onClick={this.closeModal}
              aria-label="Close modal"
            >
              ×
            </button>
            <div className="exit-modal-content">
              {children}
            </div>
          </div>
        </>
      )
    }

    // Default render with form
    return (
      <>
        <div className="exit-modal-overlay" onClick={this.closeModal} />
        <div className="exit-modal-container">
          <button 
            className="exit-modal-close" 
            onClick={this.closeModal}
            aria-label="Close modal"
          >
            ×
          </button>
          <div className="exit-modal-content">
            {this.props.renderContent ? this.props.renderContent({
              firstName,
              email,
              isSubmitting,
              onInputChange: this.handleInputChange,
              onSubmit: this.handleSubmit
            }) : null}
          </div>
        </div>
      </>
    )
  }
}

ExitIntentModal.propTypes = {
  enabled: PropTypes.bool,
  children: PropTypes.node,
  renderContent: PropTypes.func,
  redirectUrl: PropTypes.string
}

ExitIntentModal.defaultProps = {
  enabled: true
}

export default ExitIntentModal
